import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useEscapeKey } from '../hooks/useEscapeKey';
import '../styles/ReactivatePremiumModal.css';

/**
 * ReactivatePremiumModal Component
 *
 * Modal shown when a user with inactive premium features tries to access their saved premium content.
 * Content remains saved but is not visible/accessible until they reactivate premium.
 *
 * Props:
 * - isOpen: boolean - Whether the modal is visible
 * - onClose: function - Handler for "Maybe Later" (dismiss)
 * - featureType: 'trip' | 'link' | 'share' | 'socialLinks' | 'bioLink' - Which feature triggered the modal
 */

const FEATURE_CONFIG = {
  trip: {
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M3 6h18M3 12h18M3 18h18" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="7" cy="6" r="2" fill="currentColor" stroke="none" />
        <circle cx="17" cy="12" r="2" fill="currentColor" stroke="none" />
        <circle cx="10" cy="18" r="2" fill="currentColor" stroke="none" />
      </svg>
    ),
    message: 'Your trip is saved. Reactivate Premium to view and share it.',
  },
  link: {
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    message: 'Your link is saved. Reactivate Premium to view and share it.',
  },
  share: {
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="18" cy="5" r="3" />
        <circle cx="6" cy="12" r="3" />
        <circle cx="18" cy="19" r="3" />
        <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    message: 'Your share link is saved. Reactivate Premium to make it accessible again.',
  },
  socialLinks: {
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    message: 'Your links are saved. Reactivate Premium to view and share them.',
  },
  bioLink: {
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    message: 'Your bio link is saved. Reactivate Premium to make it visible again.',
  },
};

function ReactivatePremiumModal({ isOpen, onClose, featureType = 'link' }) {
  const navigate = useNavigate();
  useEscapeKey(isOpen, onClose);

  if (!isOpen) return null;

  const config = FEATURE_CONFIG[featureType] || FEATURE_CONFIG.link;

  const handleReactivate = () => {
    onClose();
    navigate('/premium');
  };

  return (
    <div className="reactivate-modal-overlay" onClick={onClose}>
      <div className="reactivate-modal" onClick={(e) => e.stopPropagation()}>
        <button className="reactivate-modal-close" onClick={onClose} aria-label="Close">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <div className="reactivate-modal-icon">
          {config.icon}
        </div>

        <h2 className="reactivate-modal-title">Reactivate Premium</h2>

        <p className="reactivate-modal-description">
          {config.message}
        </p>

        <div className="reactivate-modal-actions">
          <button className="reactivate-modal-primary" onClick={handleReactivate}>
            Reactivate Premium
          </button>
          <button className="reactivate-modal-secondary" onClick={onClose}>
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReactivatePremiumModal;
